import {
  BOARD_SIZE,
  MAX_STACK_HEIGHT,
  coordinate,
  type GameState,
  type Move,
  type Player,
} from "../game";

const CELL_COUNT = BOARD_SIZE * BOARD_SIZE;
const REMOVED = 0;
const EMPTY_FIELD = 1;

export type FastPlayer = 0 | 1;

export type FastPosition = Readonly<{
  cells: Uint8Array;
  active: FastPlayer;
  winner: -1 | FastPlayer;
}>;

export function fastPlayer(player: Player): FastPlayer {
  return player === "black" ? 0 : 1;
}

function stackHeight(cell: number): number {
  return cell === REMOVED ? -1 : 31 - Math.clz32(cell);
}

function topPlayer(cell: number): FastPlayer | -1 {
  const height = stackHeight(cell);
  return height <= 0 ? -1 : (((cell >> (height - 1)) & 1) as FastPlayer);
}

function containsPlayer(cell: number, player: FastPlayer): boolean {
  const height = stackHeight(cell);
  if (height <= 0) return false;
  const stackBits = cell - (1 << height);
  for (let index = 0; index < height; index += 1) {
    if (((stackBits >> index) & 1) === player) return true;
  }
  return false;
}

function popDisc(cell: number): number {
  const height = stackHeight(cell);
  if (height <= 0) throw new Error("Cannot pop an empty fast-position field.");
  const remainingHeight = height - 1;
  const stackBits = cell - (1 << height);
  const mask = (1 << remainingHeight) - 1;
  return (1 << remainingHeight) | (stackBits & mask);
}

function pushDisc(cell: number, player: FastPlayer): number {
  const height = stackHeight(cell);
  if (height < 0 || height >= MAX_STACK_HEIGHT) {
    throw new Error("Cannot push onto an unavailable fast-position field.");
  }
  const stackBits = cell - (1 << height);
  return (1 << (height + 1)) | stackBits | (player << height);
}

function encodeStack(stack: readonly Player[]): number {
  let encoded = 1 << stack.length;
  for (let index = 0; index < stack.length; index += 1) {
    const disc = stack[index];
    if (disc === undefined) throw new Error("Cannot encode a sparse canonical stack.");
    encoded |= fastPlayer(disc) << index;
  }
  return encoded;
}

export function createFastPosition(state: GameState): FastPosition {
  const cells = new Uint8Array(CELL_COUNT);
  for (let index = 0; index < CELL_COUNT; index += 1) {
    const cell = state.cells[index];
    cells[index] = cell?.kind === "field" ? encodeStack(cell.stack) : REMOVED;
  }
  return {
    cells,
    active: fastPlayer(state.activePlayer),
    winner: state.outcome === null ? -1 : fastPlayer(state.outcome.winner),
  };
}

export function encodeFastMove(move: Move): number {
  const from = move.from.row * BOARD_SIZE + move.from.column;
  return (
    from |
    ((move.to.row + 1) << 5) |
    ((move.to.column + 1) << 8) |
    ((move.kind === "passage" ? 1 : 0) << 11)
  );
}

export function decodeFastMove(move: number): Move {
  const from = move & 31;
  return Object.freeze({
    kind: ((move >> 11) & 1) === 1 ? "passage" : "normal",
    from: coordinate(Math.floor(from / BOARD_SIZE), from % BOARD_SIZE),
    to: coordinate(((move >> 5) & 7) - 1, ((move >> 8) & 7) - 1),
  });
}

function rowProgress(player: FastPlayer, row: number): number {
  return player === 0 ? BOARD_SIZE - 1 - row : row;
}

function frontier(position: FastPosition, player: FastPlayer): number {
  const opponent = (1 - player) as FastPlayer;
  let result = -1;
  for (let index = 0; index < CELL_COUNT; index += 1) {
    if (!containsPlayer(position.cells[index] ?? REMOVED, opponent)) continue;
    result = Math.max(result, rowProgress(player, Math.floor(index / BOARD_SIZE)));
  }
  return result;
}

function isPassage(
  player: FastPlayer,
  fromRow: number,
  fromColumn: number,
  toRow: number,
  toColumn: number,
  targetFrontier: number,
): boolean {
  const targetOnBoard = toRow >= 0 && toRow < BOARD_SIZE && toColumn >= 0 && toColumn < BOARD_SIZE;
  return (
    targetFrontier >= 0 &&
    toRow === fromRow + (player === 0 ? -1 : 1) &&
    toColumn >= -1 &&
    toColumn <= BOARD_SIZE &&
    (targetOnBoard || toColumn === fromColumn) &&
    rowProgress(player, toRow) > targetFrontier
  );
}

export function generateFastMoves(position: FastPosition): number[] {
  if (position.winner !== -1) return [];
  const moves: number[] = [];
  const currentFrontier = frontier(position, position.active);
  for (let from = 0; from < CELL_COUNT; from += 1) {
    if (topPlayer(position.cells[from] ?? REMOVED) !== position.active) continue;
    const fromRow = Math.floor(from / BOARD_SIZE);
    const fromColumn = from % BOARD_SIZE;
    for (let rowOffset = -1; rowOffset <= 1; rowOffset += 1) {
      for (let columnOffset = -1; columnOffset <= 1; columnOffset += 1) {
        if (rowOffset === 0 && columnOffset === 0) continue;
        const toRow = fromRow + rowOffset;
        const toColumn = fromColumn + columnOffset;
        const passage = isPassage(
          position.active,
          fromRow,
          fromColumn,
          toRow,
          toColumn,
          currentFrontier,
        );
        const targetOnBoard =
          toRow >= 0 && toRow < BOARD_SIZE && toColumn >= 0 && toColumn < BOARD_SIZE;
        const destination = targetOnBoard
          ? (position.cells[toRow * BOARD_SIZE + toColumn] ?? REMOVED)
          : REMOVED;
        const height = stackHeight(destination);
        const validNormalDestination = height >= 0 && height < MAX_STACK_HEIGHT;
        if (!passage) {
          if (!validNormalDestination) continue;
        }
        moves.push(from | ((toRow + 1) << 5) | ((toColumn + 1) << 8) | ((passage ? 1 : 0) << 11));
      }
    }
  }
  return moves;
}

function hasNeighbor(cells: Uint8Array, index: number): boolean {
  const row = Math.floor(index / BOARD_SIZE);
  const column = index % BOARD_SIZE;
  for (let rowOffset = -1; rowOffset <= 1; rowOffset += 1) {
    for (let columnOffset = -1; columnOffset <= 1; columnOffset += 1) {
      if (rowOffset === 0 && columnOffset === 0) continue;
      const nextRow = row + rowOffset;
      const nextColumn = column + columnOffset;
      if (
        nextRow >= 0 &&
        nextRow < BOARD_SIZE &&
        nextColumn >= 0 &&
        nextColumn < BOARD_SIZE &&
        cells[nextRow * BOARD_SIZE + nextColumn] !== REMOVED
      ) {
        return true;
      }
    }
  }
  return false;
}

function removeOrphans(cells: Uint8Array): void {
  const orphaned: number[] = [];
  for (let index = 0; index < CELL_COUNT; index += 1) {
    if (cells[index] !== REMOVED && !hasNeighbor(cells, index)) orphaned.push(index);
  }
  for (const index of orphaned) cells[index] = REMOVED;
}

export function applyFastMove(position: FastPosition, move: number): FastPosition {
  const cells = position.cells.slice();
  const from = move & 31;
  const passage = ((move >> 11) & 1) === 1;
  const source = cells[from] ?? REMOVED;
  cells[from] = popDisc(source);
  if (!passage) {
    const toRow = ((move >> 5) & 7) - 1;
    const toColumn = ((move >> 8) & 7) - 1;
    const to = toRow * BOARD_SIZE + toColumn;
    cells[to] = pushDisc(cells[to] ?? REMOVED, position.active);
  }
  if (cells[from] === EMPTY_FIELD) {
    cells[from] = REMOVED;
    removeOrphans(cells);
  }
  if (passage) return { cells, active: position.active, winner: position.active };

  const active = (1 - position.active) as FastPlayer;
  const next: FastPosition = { cells, active, winner: -1 };
  if (generateFastMoves(next).length === 0) {
    return { cells, active, winner: position.active };
  }
  return next;
}

export function fastPositionKey(position: FastPosition): string {
  return String.fromCharCode(...position.cells, position.active, position.winner + 1);
}

export function fastRepetitionKey(position: FastPosition): string {
  return String.fromCharCode(...position.cells, position.active);
}

export function evaluateFastMaterial(position: FastPosition, perspective: FastPlayer): number {
  let score = 0;
  for (let index = 0; index < CELL_COUNT; index += 1) {
    const cell = position.cells[index] ?? REMOVED;
    const height = stackHeight(cell);
    if (height <= 0) continue;
    const row = Math.floor(index / BOARD_SIZE);
    const bits = cell - (1 << height);
    const controller = topPlayer(cell);
    let neighbors = 0;
    const column = index % BOARD_SIZE;
    for (let rowOffset = -1; rowOffset <= 1; rowOffset += 1) {
      for (let columnOffset = -1; columnOffset <= 1; columnOffset += 1) {
        if (rowOffset === 0 && columnOffset === 0) continue;
        const nextRow = row + rowOffset;
        const nextColumn = column + columnOffset;
        if (
          nextRow >= 0 &&
          nextRow < BOARD_SIZE &&
          nextColumn >= 0 &&
          nextColumn < BOARD_SIZE &&
          position.cells[nextRow * BOARD_SIZE + nextColumn] !== REMOVED
        ) {
          neighbors += 1;
        }
      }
    }
    for (let stackIndex = 0; stackIndex < height; stackIndex += 1) {
      const disc = ((bits >> stackIndex) & 1) as FastPlayer;
      const sign = disc === perspective ? 1 : -1;
      score += sign * rowProgress(disc, row) * 26;
      score += sign * (stackIndex === height - 1 ? 34 : 10);
    }
    if (controller !== -1) {
      const sign = controller === perspective ? 1 : -1;
      score += sign * height * 12;
      score += sign * Math.min(neighbors, 4) * 3;
      if (neighbors <= 2) score -= sign * 18;
    }
  }
  return score;
}
